import React, { createContext, useContext, useState } from 'react'

export type validationResult = {
	valid: boolean
	error: string
}

export type validatorFunc = {
	(value: string): validationResult[]
}

export interface Validator {
	id: string
	validate: validatorFunc
	// The ref object itself, not its current value: registration happens once on
	// mount, so a snapshot taken then would go stale (or be null) as soon as the
	// field re-renders.
	ref: React.RefObject<any>
	// The element wrapping the field on the page, scrolled to when it is the
	// first one to fix.
	container: React.RefObject<HTMLElement | null>
	setter: any
}

export interface ValidatorContextProps {
	register: (validator: Validator) => void
	unregister: (id: string) => void
	validate: () => boolean
}

interface ValidatorProviderProps {
	children: React.ReactNode
}

// Fields register as they mount, so a field revealed by a later choice comes
// after the ones below it: the first to fix is found from the page order.
function scrollToFirst(elements: HTMLElement[]) {
	if (elements.length === 0) return
	const first = elements.reduce((top, element) =>
		element.compareDocumentPosition(top) & Node.DOCUMENT_POSITION_FOLLOWING ? element : top,
	)
	first.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const ValidatorContext = createContext<ValidatorContextProps | null>(null)

const ValidatorProvider: React.FC<ValidatorProviderProps> = ({ children }) => {
	const [validators, setValidators] = useState<{ [id: string]: Validator }>({})

	const register = (validator: Validator) => {
		const newValidator: { [id: string]: Validator } = { [validator.id]: validator }
		setValidators(validators => ({ ...validators, ...newValidator }))
	}

	const unregister = (id: string) => {
		setValidators(validators => {
			const next = { ...validators }
			delete next[id]
			return next
		})
	}

	const validate = (): boolean => {
		let keys: keyof typeof validators
		let isValid = true
		const invalidContainers: HTMLElement[] = []

		for (keys in validators) {
			const validator = validators[keys]
			const field = validator.ref.current
			// A field whose ref is empty is not on screen, so there is nothing to
			// check. Anything mounted must be validated.
			if (!field) continue

			const results = validator.validate(field.value)
			const errors = results.reduce<string[]>((acc: string[], result: validationResult) => {
				return result.valid ? acc : [...acc, result.error]
			}, [])

			if (errors.length > 0) {
				isValid = false
				if (validator.container.current) invalidContainers.push(validator.container.current)
			}

			validator.setter(errors)
		}

		scrollToFirst(invalidContainers)
		return isValid
	}

	return (
		<ValidatorContext.Provider value={{ register, unregister, validate }}>{children}</ValidatorContext.Provider>
	)
}

function useValidatorContext() {
	const context = useContext(ValidatorContext) as ValidatorContextProps

	if (context === undefined) {
		throw new Error('useValidatorContext must be used within a ValidatorProvider')
	}
	return context
}

export { ValidatorProvider, useValidatorContext }
export type { Validator as ValidatorType }
